'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type ContextType, useContext, useEffect, useMemo, useState } from 'react';

import { JourneyCharacteristicType, JourneyType, ProductCharacteristicType, ProductType } from '@bytel/leviator';
import { CartContext } from '@bytel/leviator/react';
import { Container, Section, Spacer, SpacerSize, TitleMarkup, TrilogyColor, View } from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';
import { getLocalDataIfNotExpired } from '@services/storage';

import { getServerSession } from '@actions/get-session';

import { ProductsItem } from '@app-types/api/statics';
import { EligibilityConfigType } from '@app-types/config';
import {
    EligibilityError,
    FastPass,
    IRudiApiRechercherAdresse,
    LmPartners,
    WetoEndEvent,
} from '@app-types/eligibility';
import { FaqConfig } from '@app-types/eligibility/config';
import { FaiTabsEnum, TabConfig } from '@app-types/home-fai';
import { MobilePlanType, OffersByCategoryCode } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { FaiUpsellModalContainer } from '@components/fai-upsell-modal';
import { Faq } from '@components/faq';
import { Legals } from '@components/legals';
import { SSIBlock } from '@components/ssi-block';
import { TriggerLogin } from '@components/trigger-login';

import { replaceWindowUrl } from '@helpers/document';
import { LegalsSlugType, partials } from '@helpers/partials';
import { paths } from '@helpers/path';
import { sendPageViewTracking, sendTracking } from '@helpers/tracking';

import {
    ELIGIBILITY_ERROR_CODE,
    ELIGIBILITY_STEP_NAME,
    ELIGIBILITY_STEP_STATUS,
    TYPE_ELIGIBILITY,
} from '@constants/eligibility';
import { ELIGIBILITY_JOURNEY, ELIGIBILITY_STORAGE_KEY } from '@constants/provider';
import { MILLIS_SECONDS_IN_A_DAY } from '@constants/time';

import { getOptions } from '@eligibility/actions';
import {
    JourneyConfigType,
    getStoredGcpData,
    isShortListHiddenByDefault,
    removeStoredGcpData,
    setMixedCartStorage,
    useEligibility,
} from '@eligibility/helpers';

import { getAccessKeyPartner, getReferrerDomain } from '../_helpers/eligibility-referrer';
import { initializeEligibilityStorage } from '../_helpers/eligibility-storage';
import { EligibilityChecker } from './eligibility-checker';
import { EligibilityResult } from './eligibility-result';
import { EligibilityComparatorStickyBar } from './eligibility-result/comparator-sticky-bar';
import { EligibilityTitle } from './eligibility-title';
import { EligibilityWidget } from './eligibility-widget';

interface EligibilityProps {
    type?: TYPE_ELIGIBILITY;
    journeyConfig: JourneyConfigType;
    eligibilityConfig: EligibilityConfigType;
    offersByCategory: OffersByCategoryCode;
    staticsData: ProductsItem[];
    mobilePlans?: MobilePlanType[];
    tabConfig: TabConfig;
    defaultTab: FaiTabsEnum;
    gencodesByCategoryCode?: Record<string, string[]>;
}

const appConfig = getAppConfig();

type CartContextValue = ContextType<typeof CartContext>;

async function resolveFaiNeededGencodes(gencodes: string[]) {
    if (gencodes.length === 0) {
        return { faiGencodes: [], mobileGencodes: [] };
    }

    try {
        const products = await getOptions(gencodes);
        const resolved = { faiGencodes: [] as string[], mobileGencodes: [] as string[] };

        for (const gencode of gencodes) {
            const product = products[gencode];
            const resolvedGencode = product?.gencode ?? gencode;

            if (product?.type_id.startsWith('plan_')) {
                resolved.mobileGencodes.push(resolvedGencode);
            } else {
                resolved.faiGencodes.push(resolvedGencode);
            }
        }

        return resolved;
    } catch (error) {
        console.error('[cart] Could not look up needed element products', error);
        return { faiGencodes: gencodes, mobileGencodes: [] };
    }
}

function getParamValues(value: string | null): string[] {
    return value ? value.split(',') : [];
}

async function addMobileProductsToCart(
    mutation: CartContextValue['addProductsToCartMutation'],
    planId: string | null,
    optionIds: string[],
    neededProducts: string[],
    mobileGencodes: string[],
) {
    let gencodes = [...neededProducts, ...mobileGencodes];

    if (planId) {
        gencodes = [planId, ...optionIds, ...gencodes];
    }

    if (gencodes.length === 0) {
        return;
    }

    try {
        await mutation.mutateAsync({ journey: JourneyType.MOBILE_ACQUISITION, gencodes });
    } catch (error) {
        console.error('Could not add to cart', error);
    }
}

function redirectToCart(cart: CartContextValue['cart'], gamme: string | null) {
    if (location.search.includes('gamme')) {
        const fai = cart
            ?.getJourneyOfType(JourneyType.FAI_ACQUISITION)
            ?.getProductsByType(ProductType.MAIN_FAI_OFFER)?.[0];

        if (fai?.metadata?.technology && gamme?.includes(fai.metadata.technology.toLowerCase())) {
            sendTracking(false, {
                action: 'Clic_impression',
                category: 'Resultat_eligibilite_direct_panier',
                name: 'Direct_panier_OK',
            });
        }

        const newUrl = location.href.replace('gamme', 'focus');
        replaceWindowUrl(newUrl);
    }

    globalThis.location.assign(paths.cart);
}

export function Eligibility({
    journeyConfig,
    eligibilityConfig,
    offersByCategory,
    staticsData,
    tabConfig,
    defaultTab,
    gencodesByCategoryCode,
}: Readonly<EligibilityProps>) {
    const { cart, addProductsToCartMutation, addCharacteristicToJourneyMutation } = useContext(CartContext);
    const router = useRouter();
    const searchParams = useSearchParams();
    const pathname = usePathname();

    const [type, setType] = useState<TYPE_ELIGIBILITY>(TYPE_ELIGIBILITY.SEARCH);
    const [fastPass, setFastPass] = useState<FastPass>();
    const [user, setUser] = useState<UserType | undefined>();

    const lmPartnerData = useMemo(() => {
        if (journeyConfig.accessKeyPartnerConfigKey && typeof window !== 'undefined') {
            const accessKeyPartners = appConfig[journeyConfig.accessKeyPartnerConfigKey] as LmPartners;
            const accessKeyPartner = getAccessKeyPartner(
                searchParams.get('key'),
                searchParams.get('partner'),
                accessKeyPartners,
            );
            const hasAccessKeyParams = searchParams.has('key') || searchParams.has('partner');
            const isAccessKeyRequired = !journeyConfig.referrerConfigKey;
            const referrer = isAccessKeyRequired
                ? (getLocalDataIfNotExpired<string | null>(ELIGIBILITY_STORAGE_KEY.LM_PARTNER) ?? getReferrerDomain())
                : getReferrerDomain();

            const partner =
                accessKeyPartner ??
                (isAccessKeyRequired && hasAccessKeyParams
                    ? undefined
                    : accessKeyPartners.find((p) => referrer?.includes(p.referrer)));
            journeyConfig.tracking.site =
                partner && !partner.defaultTrackingSite ? `Boutique_${partner?.value}` : 'Boutique';
            if (partner?.value) {
                journeyConfig.tracking.variables = {
                    ...journeyConfig.tracking.variables,
                    partenaire: partner.value,
                };
            }

            return partner;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [journeyConfig.name, searchParams.toString()]);

    const currentCategoryGencodes = useMemo(
        () => gencodesByCategoryCode?.[journeyConfig.offerCategoryCodeToShow]?.join(','),
        [gencodesByCategoryCode, journeyConfig.offerCategoryCodeToShow],
    );
    const planId = searchParams.get('plan_id');

    const addToCart = async (
        gencodes: string[],
        idPanierCorbis: string,
        redirect: boolean = false,
        callback?: () => void,
        homeZone?: string,
    ) => {
        const principalGencode = gencodes[0];
        const { faiGencodes: resolvedFaiNeededGencodes, mobileGencodes } = await resolveFaiNeededGencodes(
            gencodes.slice(1),
        );
        const faiGencodes = [...gencodes.slice(0, 1), ...resolvedFaiNeededGencodes];
        const neededProducts = planId ? getParamValues(searchParams.get('needed_products')) : [];

        if (searchParams.get('fai_options')) {
            faiGencodes.push(searchParams.get('fai_options')!);
        }

        try {
            await addCharacteristicToJourneyMutation.mutateAsync({
                journey: JourneyType.FAI_ACQUISITION,
                characteristic: { key: JourneyCharacteristicType.FAI_ORDER_CART_ID, value: idPanierCorbis },
            });

            const productCharacteristics =
                homeZone && principalGencode
                    ? { [principalGencode]: [{ key: ProductCharacteristicType.HOME_ZONE, value: homeZone }] }
                    : undefined;

            const updatedCart = await addProductsToCartMutation.mutateAsync({
                journey: JourneyType.FAI_ACQUISITION,
                gencodes: faiGencodes,
                productCharacteristics,
            });

            const fai = updatedCart
                .getJourneyOfType(JourneyType.FAI_ACQUISITION)
                ?.getProductsByType(ProductType.MAIN_FAI_OFFER)[0];
            const one = appConfig.defaultPlans.one;
            const isOneFai =
                fai?.metadata?.rangeNg === one.rangeNg && fai?.metadata?.commercialSeries === one.commercialSeries;
            let mobilePlanId = planId === one.planGencode && !isOneFai ? null : planId;
            if (isOneFai && !mobilePlanId) {
                mobilePlanId = one.planGencode || null;
            }

            await addMobileProductsToCart(
                addProductsToCartMutation,
                mobilePlanId,
                getParamValues(searchParams.get('options_id')),
                neededProducts,
                mobileGencodes,
            );
            callback?.();
            if (redirect) {
                redirectToCart(cart, searchParams.get('gamme'));
            }
        } catch {
            console.error(`Could not add ${faiGencodes.join(', ')} to cart.`);
        }
    };

    const eligibility = useEligibility(
        eligibilityConfig,
        journeyConfig,
        addToCart,
        searchParams,
        staticsData,
        user,
        lmPartnerData,
    );

    const shouldShowMinus26Alert =
        journeyConfig.name === ELIGIBILITY_JOURNEY.MOINS_26_ANS ||
        eligibility?.shortList?.rangeNgCommercialSeries?.includes('ss_medium_xgbox');

    const handleResetAddress = () => {
        eligibility.onResetAddress();
        setFastPass(undefined);
        const params = new URLSearchParams(searchParams.toString());
        params.delete('from-widget');

        router.replace(`${pathname}${params.size > 0 ? '?' + params.toString() : ''}`);
    };

    const handleWidgetParams = () => {
        eligibility.setIsFirstStep(false);
        eligibility.setCurrentStep(ELIGIBILITY_STEP_NAME.LOADING_SEARCH);

        let eligData: IRudiApiRechercherAdresse;
        if (searchParams.get('from-widget')) {
            eligData = JSON.parse(decodeURI(searchParams.get('from-widget') ?? ''));
        } else {
            eligData = getStoredGcpData();
        }
        eligibility.setIsMapFirstStep(true);

        try {
            eligibility.setAddress(eligData);
        } catch {
            setType(TYPE_ELIGIBILITY.SEARCH);
            eligibility.setCurrentStep(ELIGIBILITY_STEP_NAME.GMAP);
        }

        removeStoredGcpData();
    };

    const handleError = (error?: EligibilityError) => {
        eligibility.setError(error);
        if (error !== undefined) {
            eligibility.setIsFirstStep(false);
        }

        switch (error?.code) {
            case ELIGIBILITY_ERROR_CODE.ND_CLIENT_EXISTANT:
                //     this.router.navigate([`${this.providerConfig.CONFIG.baseUrl}/${STEP.ERROR.ROUTE}`], {
                //         skipLocationChange: true,
                //     });
                break;
            case ELIGIBILITY_ERROR_CODE.TECHNICAL:
                sendPageViewTracking(false, {
                    ...journeyConfig.tracking,
                    variant: {
                        etape_eligibilite: 'Eligibilite_ko_erreur_technique',
                    },
                    variables: {
                        testParAdresse: type === TYPE_ELIGIBILITY.LINEAR || type === TYPE_ELIGIBILITY.SEARCH,
                    },
                });
                break;
        }
    };

    const handleEnd = async (event: WetoEndEvent['event']) => {
        eligibility.setCurrentStepStatus(ELIGIBILITY_STEP_STATUS.INVALID);
        await eligibility.setResults(type, event.result);
    };

    useEffect(() => {
        const expirationDate = Date.now() + MILLIS_SECONDS_IN_A_DAY;
        initializeEligibilityStorage(journeyConfig, searchParams, pathname, appConfig, expirationDate);
    }, []);

    useEffect(() => {
        getServerSession()
            .then((session) => {
                setUser(session?.user as UserType | undefined);
            })
            .catch(() => {
                setUser(undefined);
            });
    }, []);

    useEffect(() => {
        const zipCode = searchParams.get('zipCode');
        const inseeCode = searchParams.get('inseeCode');
        const rivoliCode = searchParams.get('rivoliCode');
        const streetNumber = searchParams.get('streetNumber');
        const extension = searchParams.get('extension');
        const fromWidget = searchParams.get('from-widget');

        if (zipCode && inseeCode && rivoliCode) {
            setFastPass({
                zipCode,
                inseeCode,
                rivoliCode,
                streetNumber: streetNumber || '0',
                extension: extension ?? undefined,
            });
        } else if (fromWidget !== null) {
            handleWidgetParams();
        } else {
            eligibility.setCurrentStep(ELIGIBILITY_STEP_NAME.SINGLE_SEARCH_ELIG);
        }

        // panier-mixte is enabled by default; only the URL param can disable it
        const mixedCartParam = searchParams.get('panierMixte');
        if (mixedCartParam !== null) {
            setMixedCartStorage(mixedCartParam !== 'false');
        }
    }, [searchParams.toString()]);

    const faqConfig: FaqConfig = useMemo(() => {
        if (journeyConfig.name === ELIGIBILITY_JOURNEY.LM && lmPartnerData) {
            return {
                title: 'Vos questions fréquentes sur l’éligibilité à la fibre',
                path: `offres-internet_${lmPartnerData?.value}`,
            } as FaqConfig;
        }
        return journeyConfig.faq;
    }, [journeyConfig, lmPartnerData]);

    const legalsConfig: LegalsSlugType = useMemo(() => {
        if (journeyConfig.name === ELIGIBILITY_JOURNEY.LM && lmPartnerData) {
            return `offres-internet_${lmPartnerData?.value}` as LegalsSlugType;
        }
        return journeyConfig.legalsPath;
    }, [journeyConfig, lmPartnerData]);

    return (
        <>
            <Section
                className='has-pattern-light'
                backgroundColor={TrilogyColor.NEUTRAL_FADE}
                style={{ minHeight: '90vh' }}
            >
                <Container>
                    <View style={{ minHeight: eligibility.results ? 'auto' : '60vh' }}>
                        <EligibilityTitle
                            config={journeyConfig}
                            shortList={
                                !eligibility.results && journeyConfig.wordings.overrideShortListTitle
                                    ? undefined
                                    : eligibility.shortList
                            }
                            results={eligibility.results}
                        />
                        {!eligibility.results && (
                            <EligibilityChecker
                                type={type}
                                config={journeyConfig}
                                setType={setType}
                                currentStep={eligibility.currentStep}
                                currentStepStatus={eligibility.currentStepStatus}
                                isFirstStep={eligibility.isFirstStep}
                                handleEligibilityChange={eligibility.handleEligibilityChange}
                            >
                                <EligibilityWidget
                                    type={type}
                                    config={journeyConfig}
                                    fastPass={fastPass}
                                    address={eligibility.address}
                                    isMapFirstStep={eligibility.isMapFirstStep}
                                    isFirstStep={eligibility.isFirstStep}
                                    currentStepStatus={eligibility.currentStepStatus}
                                    currentStep={eligibility.currentStep}
                                    error={eligibility.error}
                                    handleStepStatusChange={eligibility.handleStepStatusChange}
                                    handleError={handleError}
                                    handleStepChange={eligibility.handleStepChange}
                                    handleEnd={handleEnd}
                                    user={user}
                                    mapAllowedOfferIds={currentCategoryGencodes}
                                />
                            </EligibilityChecker>
                        )}
                        {!eligibility.results && shouldShowMinus26Alert && (
                            <>
                                <Spacer size={SpacerSize.FIVE} />
                                <SSIBlock url={partials.MARKETING.ELIGIBILITY_26_YEAR_OLD} />
                            </>
                        )}
                    </View>

                    {eligibility.results && (
                        <EligibilityResult
                            type={type}
                            onResetAddress={handleResetAddress}
                            journeyConfig={journeyConfig}
                            results={eligibility.results}
                            addToCart={addToCart}
                            shortList={eligibility.shortList}
                            openOfferUpsellModal={eligibility.openOfferUpsellModal}
                            offersByCategory={offersByCategory}
                            planId={planId || undefined}
                            tabConfig={tabConfig}
                            defaultTab={defaultTab}
                        />
                    )}
                </Container>
                <Spacer size={SpacerSize.TEN} />
                {!eligibility.results &&
                    !journeyConfig.faq.isHiddenBeforeTest &&
                    !isShortListHiddenByDefault(eligibility.shortList, journeyConfig.productsToUpsell, staticsData) && (
                        <Container>
                            <Faq
                                key={faqConfig.path}
                                titleMarkup={TitleMarkup.H3}
                                title={faqConfig.title}
                                url={faqConfig.path}
                            />
                        </Container>
                    )}
                <TriggerLogin />
                <Container>{eligibility.results && <Legals id='post-elig-legals' url={legalsConfig} />}</Container>
                {eligibility.upsellModal && <FaiUpsellModalContainer {...eligibility.upsellModal} />}
            </Section>
            <EligibilityComparatorStickyBar showComparatorSelection={journeyConfig.showComparatorSelection} />
        </>
    );
}
